import type { JSX, ReactNode } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SortableTableHead } from '@/shared/components/SortableTableHead';
import type { SortState } from '@/shared/types/table-filters.types';
import { ExperienceStatusBadge } from './ExperienceStatusBadge';
import { ExperienceActions } from './ExperienceActions';
import { ExperienceExpandableRow } from './ExperienceExpandableRow';
import type { Experience, ExperienceStatus } from '../types/experience.types';

interface SortProps {
  sort: SortState | null;
  onToggleSort: (key: string) => void;
}

interface ExperienceTableProps extends SortProps {
  experiences: Experience[];
  onEdit: (experience: Experience) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: ExperienceStatus) => void;
  renderExpandedContent: (experience: Experience) => ReactNode;
}

const SORTABLE_HEADERS = [
  { key: 'companyName', label: 'Company' },
  { key: 'jobTitle', label: 'Job Title' },
  { key: 'startDate', label: 'Period' },
];

function ExperienceTableHeader({ sort, onToggleSort }: SortProps): JSX.Element {
  return (
    <TableHeader>
      <TableRow>
        <TableHead className="w-[40px]" />
        {SORTABLE_HEADERS.map((header) => (
          <SortableTableHead
            key={header.key}
            columnKey={header.key}
            label={header.label}
            sort={sort}
            onToggleSort={onToggleSort}
          />
        ))}
        {/* Duration is derived from the dates, not a stored field, so it is not sortable. */}
        <TableHead>Duration</TableHead>
        <SortableTableHead
          columnKey="status"
          label="Status"
          sort={sort}
          onToggleSort={onToggleSort}
        />
        <TableHead className="text-right">Actions</TableHead>
      </TableRow>
    </TableHeader>
  );
}

export function ExperienceTable({
  experiences,
  sort,
  onToggleSort,
  onEdit,
  onDelete,
  onStatusChange,
  renderExpandedContent,
}: ExperienceTableProps): JSX.Element {
  return (
    <Table>
      <ExperienceTableHeader sort={sort} onToggleSort={onToggleSort} />
      <TableBody>
        {experiences.map((experience) => (
          <ExperienceExpandableRow
            key={experience.id}
            experience={experience}
            expandedContent={renderExpandedContent(experience)}
            statusBadge={<ExperienceStatusBadge status={experience.status} />}
            actions={
              <ExperienceActions
                experience={experience}
                onEdit={() => onEdit(experience)}
                onDelete={() => onDelete(experience.id)}
                onStatusChange={(status) => onStatusChange(experience.id, status)}
              />
            }
          />
        ))}
      </TableBody>
    </Table>
  );
}
